// React
import { useEffect, useState } from "react"

// Third-Party Libraries
import axios from "axios"
import toast from "react-hot-toast"

// Types
import type { SelectOptionType, SuccessFetch } from "types"
import type { FieldType, MasterType, ReturnMasterType } from "../../types"

// Utils
import { useApi } from "utils"
import { defaultMasterData } from "../vars"

export function useMasterData(): ReturnMasterType {
  // Hooks
  const api = useApi()
  const [data, setData] = useState<MasterType>(defaultMasterData)
  const [isError, setError] = useState<boolean>(false)
  const [isLoading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    axios.all([
      api.get("/productsize/"),
      api.get("/categorycustomer/"),
      api.get("/productcategory/"),
      api.get("/productcolour/"),
      api.get("/productgroup/"),
      api.get("/productmaterial/"),
      api.get("/productstyle/"),
      api.get("/productunit/"),
      api.get("/product/")
    ]).then(axios.spread((
      size,
      category,
      product_category,
      colour,
      group,
      material,
      style,
      unit,
      product
    ) => {
      // Vars
      const size_value: SuccessFetch<{ payload: { mr_product_size_id: number; mr_product_size_name: string; }[] }> = size
      const category_value: SuccessFetch<{ payload: { category_customer_id: number; category_customer_name: string; }[] }> = category
      const product_category_value: SuccessFetch<{ payload: { mr_product_category_id: number; mr_product_category_name: string; }[] }> = product_category
      const colour_value: SuccessFetch<{ payload: { mr_product_colour_id: number; mr_product_colour_name: string; }[] }> = colour
      const group_value: SuccessFetch<{ payload: { mr_product_group_id: number; mr_product_group_name: string; }[] }> = group
      const material_value: SuccessFetch<{ payload: { mr_product_material_id: number; mr_product_material_name: string; }[] }> = material
      const style_value: SuccessFetch<{ payload: { mr_product_style_id: number; mr_product_style_name: string; }[] }> = style
      const unit_value: SuccessFetch<{ payload: { mr_unit_id: number; mr_unit_name: string; }[] }> = unit
      const product_value: SuccessFetch<{ payload: {
        product_id: number
        product_barcode: string
        product_code: string
      }[] }> = product
      const result: MasterType = {
        category: category_value.data.payload.map(item => ({
          label: item.category_customer_name,
          value: item.category_customer_id.toString()
        })),
        colour: colour_value.data.payload.map(item => ({
          label: item.mr_product_colour_name,
          value: item.mr_product_colour_id.toString()
        })),
        group: group_value.data.payload.map(item => ({
          label: item.mr_product_group_name,
          value: item.mr_product_group_id.toString()
        })),
        material: material_value.data.payload.map(item => ({
          label: item.mr_product_material_name,
          value: item.mr_product_material_id.toString()
        })),
        product_category: product_category_value.data.payload.map(item => ({
          label: item.mr_product_category_name,
          value: item.mr_product_category_id.toString()
        })),
        size: size_value.data.payload.map(item => ({
          label: item.mr_product_size_name,
          value: item.mr_product_size_id.toString()
        })),
        style: style_value.data.payload.map(item => ({
          label: item.mr_product_style_name,
          value: item.mr_product_style_id.toString()
        })),
        unit: unit_value.data.payload.map(item => ({
          label: item.mr_unit_name,
          value: item.mr_unit_id.toString()
        })),
        product: product_value.data.payload.map(item => ({
          label: item.product_barcode,
          value: item.product_id.toString(),
          product_code: item.product_code
        }))
      }

      setData(result)
    })).catch(() => {
      toast.error("Fetching Data Master Error")
      setError(true)
    }).finally(() => {
      setLoading(false)
    })

    return () => {
      setData(defaultMasterData)
      setError(false)
      setLoading(true)
    }

    // eslint-disable-next-line
  }, [])

  // Functions
  const appendData = (
    field: FieldType,
    value: SelectOptionType
  ) => {
    setData(prev => {
      // Vars
      const newValue = [...prev[field]]
      newValue.push(value)

      return {
        ...prev,
        [field]: newValue
      }
    })
  }

  return { data, isError, isLoading, appendData }
}